import React from "react";
import { FileText, Sparkles } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";

export default function Terms() {
  const { settings } = useOutletContext();
  const appName = settings?.general?.appName || "Sugam Store";
  const supportEmail = settings?.general?.supportEmail || "support@sugamstore.in";

  return (
    <main className="page-shell content-page policy-page">
      <div className="breadcrumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Terms & Conditions</span>
      </div>

      <div className="policy-header">
        <span className="eyebrow">
          <Sparkles size={14} /> LEGAL AGREEMENT
        </span>
        <h1>Terms & Conditions</h1>
        <p className="policy-meta">Last Updated: October 2026 · Please read carefully before ordering</p>
      </div>

      <div className="policy-container">
        <aside className="policy-nav-card">
          <h3>Table of Contents</h3>
          <nav>
            <a href="#terms-general">1. Acceptance of Terms</a>
            <a href="#terms-account">2. User Accounts & Eligibility</a>
            <a href="#terms-pricing">3. Products & Pricing</a>
            <a href="#terms-orders">4. Orders & Acceptance</a>
            <a href="#terms-payment">5. Payment & Invoicing</a>
            <a href="#terms-cancellation">6. Order Cancellation</a>
            <a href="#terms-ip">7. Intellectual Property</a>
            <a href="#terms-liability">8. Limitation of Liability</a>
            <a href="#terms-law">9. Governing Law</a>
          </nav>
        </aside>

        <article className="policy-body">
          <section id="terms-general">
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms and Conditions (“Terms”) govern your access to and use of the <strong>{appName}</strong> website, 
              services, and mobile interfaces. By placing an order or registering an account, you acknowledge that 
              you have read, understood, and agree to be bound by these Terms and our related policies.
            </p>
          </section>

          <section id="terms-account">
            <h2>2. User Accounts & Eligibility</h2>
            <p>
              You must be at least 18 years of age or accessing under the supervision of a parent or legal guardian to 
              make transactions on this site. You are responsible for maintaining the confidentiality of your mobile OTP 
              credentials and are fully accountable for all activities that occur under your account.
            </p>
          </section>

          <section id="terms-pricing">
            <h2>3. Product Information & Pricing</h2>
            <p>
              We strive to display accurate descriptions, high-resolution imagery, and current pricing for all products. 
              However, in the event of an inadvertent typographical error or system discrepancy in pricing or availability:
            </p>
            <ul>
              <li>We reserve the right to correct pricing errors and cancel orders placed at incorrect prices.</li>
              <li>Prices are in Indian Rupees (INR) and are inclusive of applicable goods and services taxes (GST).</li>
              <li>Special promotions, discount coupons, and offers are valid for limited periods as specified.</li>
            </ul>
          </section>

          <section id="terms-orders">
            <h2>4. Orders & Acceptance</h2>
            <p>
              Receipt of an order confirmation or SMS does not signify our final acceptance of your order. 
              We reserve the right at any time after receipt of your order to accept, decline, or limit quantity 
              for reasons such as inventory exhaustion, suspected fraud, or courier service unavailability.
            </p>
          </section>

          <section id="terms-payment">
            <h2>5. Payment & Invoicing</h2>
            <p>
              We accept multiple secure payment options including UPI (Google Pay, PhonePe, Paytm), Credit & Debit cards, 
              Netbanking, and Cash on Delivery (COD) where available. For online transactions, payment authorization is 
              obtained prior to dispatch.
            </p>
          </section>

          <section id="terms-cancellation">
            <h2>6. Order Cancellation</h2>
            <p>
              You may cancel an order before it has been dispatched by visiting your <Link to="/orders">Orders page</Link> or 
              contacting support. Once an order is handed over to our logistics partner, it cannot be canceled in transit 
              and must be returned according to our <Link to="/return-policy">Return Policy</Link>.
            </p>
          </section>

          <section id="terms-ip">
            <h2>7. Intellectual Property</h2>
            <p>
              All content on this site — including logos, brand marks, product imagery, copy, interfaces, and software — 
              is the exclusive property of {appName} and protected by applicable copyright and trademark laws. 
              Unauthorized copying or scraping is strictly prohibited.
            </p>
          </section>

          <section id="terms-liability">
            <h2>8. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, {appName} shall not be liable for any indirect, 
              incidental, or consequential damages resulting from the use or inability to use the platform or items purchased.
            </p>
          </section>

          <section id="terms-law">
            <h2>9. Governing Law & Jurisdiction</h2>
            <p>
              These Terms shall be interpreted and governed in accordance with the laws of India. Any disputes arising out of 
              or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in India.
            </p>
            <p>
              For any questions regarding these Terms, contact our legal helpdesk at{" "}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}
